/**
 * Admin All API Keys Page
 * ყველა API Key-ის ადმინისტრირების გვერდი
 */

import { useState } from 'react';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { useAdminApiKeys } from '../../hooks/useAdminListing';
import type { ApiKeyAdminListResponse } from '../../features/api/adminListing';

export default function AllApiKeysPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const { apiKeys, totalElements, totalPages, isLoading } = useAdminApiKeys({
    status: statusFilter || undefined,
    page,
    size: 20,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case 'DISABLED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Disabled</span>;
      case 'DELETED':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Deleted</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All API Keys</h1>
        <p className="text-gray-600">View all platform API keys</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="DISABLED">Disabled</option>
        </select>
        <div className="ml-auto text-sm text-gray-500">
          {totalElements} API keys found
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key Prefix</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domains</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {apiKeys.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No API keys found
                </td>
              </tr>
            ) : (
              apiKeys.map((key: ApiKeyAdminListResponse) => (
                <tr key={key.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{key.name}</td>
                  <td className="px-6 py-4">
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm">{key.keyPrefix}...</code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{key.userEmail || '-'}</td>
                  <td className="px-6 py-4">{getStatusBadge(key.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {key.allowedDomains.length > 0 ? key.allowedDomains.join(', ') : 'All'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {key.lastUsedAt ? formatDateTime(key.lastUsedAt) : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(key.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
